/**
 * Attribute-agnostic single-select toggle for a `PropertyFilter` chip
 * inside a `PropertyFilterGroup`. Pure and non-mutating.
 *
 * The FilterBar tree can carry any combination of root children:
 *   - an optional top-level `asin` `PropertyFilter` (owned by the
 *     Legacy textarea and URL)
 *   - one or more nested `PropertyFilterGroup`s. UI edits always
 *     collapse everything into a single nested group, but shared /
 *     hand-crafted / legacy URLs (e.g.
 *     `(and (and (range …)) (or (filter productLineId …) …) (and
 *     (filter programId …)))`) parse into multiple sibling groups —
 *     one per top-level clause. `rehydratePolarisPropertyFilter`
 *     renders them correctly, so this helper must preserve every
 *     one of them.
 *
 * Caller-provided `isMemberOfSelectGroup` predicate decides which
 * existing chips constitute the "select group" — the set of chips that
 * should be replaced by the target. That lets simple single-select
 * attributes strip by `propertyKey` while attributes that pack multiple
 * logical dimensions into one key (like `validationStatus`, which
 * prefixes the value with a bar identifier) can strip by value prefix
 * and preserve chips belonging to sibling dimensions.
 *
 * Toggle-off behavior: if the exact target chip
 * (`(newFilter.propertyKey, newFilter.value)`) already exists anywhere
 * in the tree, all select-group members are stripped and the target is
 * not re-appended.
 */

import { filterPropertyFilterTree, someLeafFilter } from "./propertyFilterTree";
import type {
  PropertyFilter,
  PropertyFilterGroup,
} from "./searchSideBarConnector";

/**
 * Returns a fresh `PropertyFilterGroup` with the target token added,
 * replaced, or toggled off per the rules above. `current` is never
 * mutated. Non-matching tokens at every depth of the tree and the
 * top-level `asin` filter are preserved untouched. When adding the
 * target, it's appended as its own sibling `PropertyFilterGroup` at
 * the root so pre-existing groups (including OR groups on
 * multi-valued attributes like `productLineId`) are left structurally
 * intact.
 */
export function toggleSingleSelectFilterToken(
  current: PropertyFilterGroup,
  newFilter: PropertyFilter,
  isMemberOfSelectGroup: (filter: PropertyFilter) => boolean
): PropertyFilterGroup {
  const isExactTargetLeaf = (f: PropertyFilter): boolean =>
    f.propertyKey === newFilter.propertyKey && f.value === newFilter.value;

  const hasTarget = someLeafFilter(current, isExactTargetLeaf);

  const strippedRoot = filterPropertyFilterTree(
    current,
    // `asin` leaves are owned by the Legacy textarea / URL and never
    // belong to a chart-driven select group, so always keep them.
    (f) => f.propertyKey !== "asin" && isMemberOfSelectGroup(f)
  );

  if (hasTarget) {
    return strippedRoot;
  }

  // Append the new chip as its own sibling `and` group at the root.
  // Adding it to any existing nested group would either mutate an
  // unrelated OR group's semantics or force an ordering assumption
  // on which group "owns" the chip. A new sibling AND under the root
  // AND is semantically equivalent (associativity) and keeps every
  // sibling group untouched.
  return {
    type: "PropertyFilterGroup",
    operation: strippedRoot.operation,
    filters: [
      ...strippedRoot.filters,
      {
        type: "PropertyFilterGroup",
        operation: "and",
        filters: [newFilter],
      },
    ],
  };
}
